/* eslint-disable radix */
/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
/* eslint-disable no-console */

import { AdAPI } from './ad-api';
import { AdReporter } from './ad-reporter';
import { saveMiniGameInfo } from './ad-save-mini-game';
import { AdUtils, safeParseJson } from './ad-utils';
import { AdInfo } from './types/ad';
import { AdVRActionType } from './types/ad-report-enum';
import { AdInteractiveMode, LandingPageShowType } from './types/ad-request-enum';
import * as TencentMiniProgramHelper from '@tencent/mini-program-helper';

// 端内打开落地页容器宽高 iPhone 8 Plus 尺寸
const WEBVIEW_WIDTH = 414;
const WEBVIEW_HEIGHT = 736;

// 端内打开落地页协议前缀
const PROTOCOL = `qqliveex://openportal/width=${WEBVIEW_WIDTH}&height=${WEBVIEW_HEIGHT}&ua=app&portalurl=`;

const SPLICING_PAGE_URL = 'https://active.v.qq.com/client-splicing-page';

// 广告点击跳转类型
export enum AdActionJumpType {
  NONE = 'none', // 无
  PC_SDK = 'pc_sdk', // PC AMS SDK
  SHORT_DRAMA = 'short_drama', // 短剧
  MINI_PROGRAM = 'mini_program', // 小程序

  LANDING_PAGE_SPLIT = 'landing_page_split', // 拼接落地页
  LANDING_PAGE_INSIDE = 'landing_page_inside', // 内置窗口拉起落地页
  LANDING_PAGE_OUTSIDE = 'landing_page_outside', // 外部窗口拉起落地页

  SCAN_WITH_CLICK = 'scan_with_click', // 点扫一体
  SCAN = 'scan', // 仅扫码

  APP_INNER_JUMP = 'app_inner_jump', // 网大链路，非扫码，内部跳转
  APP_INNER_JUMP_SCAN = 'app_inner_jump_scan', // 网大链路，扫码，内部跳转

  PC_SDK_WEGAME = 'pc_sdk_wegame', // 走PC SDK打开Wegame应用（比较特殊，需要从CGI换链）
}

export class AdAction {
  // 核心入口. canJump仅扫码链路跳转判断使用
  public static doClick = (
    adInfo: AdInfo | null,
    vrActType?: AdVRActionType,
    options: { canJump: boolean } = { canJump: true },
  ): boolean => {
    if (!adInfo) {
      return false;
    }
    if (!import.meta.env.EVO) {
      // Web端跳转先单独处理
      return this.doClickForWeb(adInfo, vrActType);
    }
    if (!vnContext?.external) {
      return false;
    }
    const jumpType = this.getActionJumpType(adInfo);
    console.log('[ad]doClick', adInfo, jumpType, vrActType, options);
    if (jumpType === AdActionJumpType.NONE) {
      return false;
    }

    if (jumpType === AdActionJumpType.PC_SDK_WEGAME) {
      return this.openWegameWithPCSDK(adInfo, vrActType);
    }

    if (jumpType === AdActionJumpType.PC_SDK) {
      return this.openPCSDK(adInfo, vrActType);
    }

    AdReporter.amsLinkReportClickWithAdInfo(adInfo);
    if (jumpType === AdActionJumpType.SHORT_DRAMA) {
      return this.openShortDrama(adInfo, vrActType);
    }

    const clickUrl = this.getClickUrl(adInfo.clickUrl, vrActType);
    // 端内拉起小程序小游戏
    if (jumpType === AdActionJumpType.MINI_PROGRAM) {
      AdReporter.addReportQueue([
        {
          url: clickUrl,
        },
      ]);
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openMiniProgram(adInfo);
    }

    if (jumpType === AdActionJumpType.LANDING_PAGE_SPLIT) {
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openLandingSplitPage(adInfo, vrActType);
    }

    if (jumpType === AdActionJumpType.LANDING_PAGE_INSIDE) {
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openLandingPageInside(adInfo, vrActType);
    }

    // 原始点击上报，没有应用内跳转链接时，直接打开落地页
    if (jumpType === AdActionJumpType.LANDING_PAGE_OUTSIDE) {
      // 进行第三方点击上报
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openClickUrlOutside(adInfo, vrActType);
    }

    // 点扫一体
    if (jumpType === AdActionJumpType.SCAN_WITH_CLICK) {
      // 进行第三方点击上报
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openClickUrlOutside(adInfo, vrActType);
    }

    // 端内打开落地页
    if (jumpType === AdActionJumpType.APP_INNER_JUMP) {
      AdReporter.addReportQueue([
        {
          url: clickUrl,
          delay: 0,
        },
      ]);
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openInAppJump(adInfo);
    }

    // 扫码链路
    if (jumpType === AdActionJumpType.APP_INNER_JUMP_SCAN && options.canJump) {
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openInAppJump(adInfo);
    }

    // 原扫码链路
    if (jumpType === AdActionJumpType.SCAN && options.canJump) {
      AdReporter.thirdClickReportWithAdInfo(adInfo);
      return this.openQrcode(adInfo);
    }

    AdReporter.thirdClickReportWithAdInfo(adInfo);
  };

  // Web端跳转，doClick有转发
  public static doClickForWeb = (
    adInfo: AdInfo | null,
    vrActType?: AdVRActionType,
  ): boolean => {
    // 点击上报链接
    let { clickUrl } = adInfo;

    const amsLinkParams = this.getHelperAmsLinkParams(adInfo);
    TencentMiniProgramHelper.amsLinkReportClick(4000000, amsLinkParams);

    const isMiniProgram = adInfo.miniProgram && !!adInfo.miniProgram.path;
    if (isMiniProgram) {
      this.openMiniProgramForWeb(adInfo, amsLinkParams);
    }

    // 判断是否有落地页地址
    if (!clickUrl) {
      return;
    }
    clickUrl = this.getClickUrl(clickUrl, vrActType);
    // 原始点击上报，没有短链时，直接打开落地页
    if (isMiniProgram) {
      AdReporter.addReportQueue([
        {
          url: clickUrl,
          delay: 0,
        },
      ]);
    } else if (!adInfo.qrCodeUrl || adInfo.enableClickScan) {
      window.open(clickUrl);
    }

    AdReporter.thirdClickReportWithAdInfo(adInfo);
    return false;
  };

  // 获取广告类型
  public static getActionJumpType(adInfo: AdInfo | null): AdActionJumpType {
    if (!adInfo || !import.meta.env.EVO || !vnContext?.external) {
      return AdActionJumpType.NONE;
    }
    const { pcSDK } = adInfo;
    if (pcSDK) {
      // 广告SDK链路
      const enablePcSdk = pcSDK.enable_pc_sdk || false;
      if (enablePcSdk) {
        const linkData = safeParseJson<any>(pcSDK.link_info);
        if (linkData?.jump_actions) {
          for (const jumpAction of linkData?.jump_actions) {
            if ((jumpAction?.web_data && !jumpAction.web_data.url)
              || (jumpAction?.download_data && jumpAction.download_data.type === 4)) {
              // 拉起Wegame
              return AdActionJumpType.PC_SDK_WEGAME;
            }
          }
        }
        return AdActionJumpType.PC_SDK;
      }
    }
    const { shortDrama } = adInfo;
    if (shortDrama) {
      // 短剧链路
      const sso = shortDrama.jump_url || '';
      if (sso) {
        return AdActionJumpType.SHORT_DRAMA;
      }
    }

    // 判断是否打开端内拉起小程序、小游戏
    const { miniProgram } = adInfo;
    // 端内拉起小程序小游戏
    if (miniProgram) {
      return AdActionJumpType.MINI_PROGRAM;
    }

    const clickReportUrl = adInfo.clickUrl;
    // 判断是否端内拉起落地页
    const landingPageShowType = adInfo.landingPage?.show_type
      ?? LandingPageShowType.LANDING_PAGE_SHOW_TYPE_UNKNOWN;
    // 判断是否拉起拼接页
    if (
      landingPageShowType === LandingPageShowType.LANDING_PAGE_SHOW_TYPE_SPLIT
    ) {
      return AdActionJumpType.LANDING_PAGE_SPLIT;
    }

    const landingPagePopupUrl = adInfo.landingPage?.terminal_popup_url ?? '';
    if (landingPagePopupUrl) {
      return AdActionJumpType.LANDING_PAGE_INSIDE;
    }

    // 判断是否有落地页地址.
    // 落地页打开本身既作为CGI上报，上报成功后会302跳转广告页面
    if (!clickReportUrl) {
      return AdActionJumpType.NONE;
    }

    // 原始点击上报，没有应用内跳转链接时，直接打开落地页
    if (!adInfo.qrCodeUrl && !adInfo.inAppJumpLink) {
      return AdActionJumpType.LANDING_PAGE_OUTSIDE;
    }

    // 点扫一体
    if (adInfo.qrCodeUrl && adInfo.enableClickScan) {
      return AdActionJumpType.SCAN_WITH_CLICK;
    }

    // 端内打开落地页
    if (adInfo.inAppJumpLink) {
      // 非扫码链路
      if (!adInfo.qrCodeUrl) {
        return AdActionJumpType.APP_INNER_JUMP;
      }

      // 扫码链路
      if (adInfo.qrCodeUrl && adInfo.inAppJumpLink) {
        return AdActionJumpType.APP_INNER_JUMP_SCAN;
      }
    }

    // 原扫码链路
    if (adInfo.qrCodeUrl && !adInfo.enableClickScan) {
      return AdActionJumpType.SCAN;
    }

    return AdActionJumpType.NONE;
  }

  public static openPCSDK(adInfo: AdInfo, vrActType?: AdVRActionType): boolean {
    const pcSDK = adInfo?.pcSDK;
    if (!pcSDK || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }
    const enablePcSdk = pcSDK.enable_pc_sdk || false;
    if (!enablePcSdk) {
      return false;
    }
    const interactiveModes = adInfo.interactiveModes || [];
    const baseUrl = 'qqlive://openadsdk/';
    // passthrough_report_info，透传的上报信息，JSON字符串
    const passthroughReportInfo = (pcSDK.passthrough_report_info || '').trim();
    // link_info，跳转结构信息，JSON字符串
    const linkInfo = (pcSDK.link_info || '').trim();
    // click_response_data，点击请求回包信息，JSON字符串
    const clickResponseData = '';
    const url = `${baseUrl}report_info=${encodeURIComponent(
      passthroughReportInfo,
    )}&link_info=${encodeURIComponent(
      linkInfo,
    )}&click_response_data=${encodeURIComponent(clickResponseData)}`;
    AdAPI.openQQLive(url);
    if (adInfo.clickUrl && interactiveModes.includes(AdInteractiveMode.IMODE_CLICK)) {
      AdReporter.cgiClickReport({
        logInfo: AdUtils.getAdPrintInfo(adInfo),
        items: [{ url: adInfo.clickUrl, delay: 0 }],
        actType: vrActType,
      });
    }
    console.log('[AdAction]openPCSDK', url);
    return true;
  }

  public static openWegameWithPCSDK(adInfo: AdInfo, vrActType?: AdVRActionType): boolean {
    const pcSDK = adInfo?.pcSDK;
    if (!pcSDK || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }
    const enablePcSdk = pcSDK.enable_pc_sdk || false;
    if (!enablePcSdk || !adInfo.clickUrl) {
      return false;
    }
    const baseUrl = 'qqlive://openadsdk/';
    // passthrough_report_info，透传的上报信息，JSON字符串
    const passthroughReportInfo = (pcSDK.passthrough_report_info || '').trim();
    // link_info，跳转结构信息，JSON字符串
    const linkInfo = (pcSDK.link_info || '').trim();
    AdReporter.cgiClickReport({
      logInfo: AdUtils.getAdPrintInfo(adInfo),
      items: [{
        url: adInfo.clickUrl.replace('__RETURN_TYPE__', '1'), delay: 0, callback: (response) => {
          if (!response?.data) {
            console.log('[AdAction]openWegameWithPCSDK-fail-no-data');
            return;
          }
          const url = `${baseUrl}report_info=${encodeURIComponent(
            passthroughReportInfo,
          )}&link_info=${encodeURIComponent(
            linkInfo,
          )}&click_response_data=${encodeURIComponent(JSON.stringify(response?.data || {}))}`;
          AdAPI.openQQLive(url);

          this.saveMiniGame(adInfo, url);
          console.log('[AdAction]openWegameWithPCSDK', response?.data, url);
        },
      }],
      actType: vrActType,
    });
    return true;
  }

  public static openShortDrama(adInfo: AdInfo, vrActType: AdVRActionType) {
    const shortDrama = adInfo?.shortDrama;
    if (!shortDrama || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }
    let sso = shortDrama.jump_url || '';
    if (!sso) {
      return false;
    }
    const params = {
      ams_link_params: JSON.stringify(adInfo.amsLinkParams || {}), // 非后台下发。AMS CGI参数
      click_action_type: vrActType, // 非后台下发。点击行为类型
      action_type: adInfo.actionType, // 非后台下发。行为类型
    };
    Object.entries(params).forEach(([key, val]) => {
      sso += `&${key}=${encodeURIComponent(val ?? '')}`;
    });
    AdAPI.openQQLive(sso);
    console.log('[AdAction]openShortDrama', sso);
    return true;
  }

  public static openMiniProgram(adInfo: AdInfo) {
    const miniProgram = adInfo?.miniProgram;
    if (!miniProgram) {
      return false;
    }
    if (!import.meta.env.EVO) {
      // Web跳转
      return this.openMiniProgramForWeb(adInfo, this.getHelperAmsLinkParams(adInfo));
    }
    if (!vnContext?.external) {
      return false;
    }

    const wxType = adInfo.actionType === 104 ? 2 : 1;
    let wxGameLink = `https://wx_game/?wxappid=${
      miniProgram.appid
    }&minigame=1&path=${encodeURIComponent(
      miniProgram.path,
    )}&scene_type=ad&wx_weapp_type=${wxType}`;

    Object.entries(adInfo.miniProgramNativeVrMap || {}).forEach(
      ([key, val]) => {
        wxGameLink += `&${key}=${JSON.stringify(val) ?? ''}`;
      },
    );
    Object.entries(adInfo.amsLinkParams || {}).forEach(([key, val]) => {
      wxGameLink += `&${key}=${val ?? ''}`;
    });

    const url = `qqlive://opengame/portalurl=${encodeURIComponent(wxGameLink)}`;
    AdAPI.openQQLive(url);

    this.saveMiniGame(adInfo, url);

    console.log('[AdAction]openMiniProgram', url);
    return true;
  }

  public static openMiniProgramForWeb(adInfo: AdInfo, amsLinkParams: TencentMiniProgramHelper.IAmsLinkParams) {
    const miniProgram = adInfo?.miniProgram;
    if (!miniProgram || import.meta.env.EVO) {
      return false;
    }
    const urlScheme = miniProgram.path;

    const key = 'SHOW-MINI-PROGRAM-POPUP-CHANNEL';
    const today = AdUtils.getTodayKeyWithNoZero();
    const str = localStorage.getItem(key);

    const showPopup = adInfo.control?.show_popup && str !== today;
    TencentMiniProgramHelper.webLaunchMiniProgram(urlScheme, amsLinkParams, {
      showPopup,
      popupAutoCloseMs: adInfo.control?.auto_close_ms,
    });

    if (showPopup) {
      localStorage.setItem(key, today);
    }
  }

  public static openLandingSplitPage(
    adInfo: AdInfo,
    vrActType: AdVRActionType,
  ) {
    if (!adInfo || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }
    const clickUrl = this.getClickUrl(adInfo.clickUrl, vrActType);
    const onlyScan = adInfo.qrCodeUrl && (!adInfo.enableClickScan || !clickUrl);
    // 是否打开二维码页面：仅扫码或者clickUrl 不存在
    const showQrCodePage = onlyScan || !clickUrl;

    const leftUrl = `${SPLICING_PAGE_URL}/video.html`;
    const rightUrl = showQrCodePage
      ? `${SPLICING_PAGE_URL}/qrcode.html`
      : clickUrl;
    AdAPI.openLandingPageWnd(
      adInfo.advertiserName,
      leftUrl,
      rightUrl,
      JSON.stringify({
        ...adInfo,
      }),
      Number(adInfo.landingPage?.right_page_width || 300),
    );

    console.log('[AdAction]openLandingSplitPage', leftUrl, rightUrl);
    return true;
  }

  public static openLandingPageInside(
    adInfo: AdInfo,
    vrActType: AdVRActionType,
  ) {
    if (!adInfo || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }
    let landingPagePopupUrl = adInfo.landingPage?.terminal_popup_url ?? '';
    if (vrActType) {
      landingPagePopupUrl = landingPagePopupUrl.replace(
        'acttype%3D1014',
        `acttype%3D${vrActType}`,
      );
    }
    AdAPI.openHintBrowser(landingPagePopupUrl);

    console.log('[AdAction]openLandingPageInside', landingPagePopupUrl);
    return true;
  }

  public static openClickUrlOutside(adInfo: AdInfo, vrActType: AdVRActionType) {
    if (!adInfo || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }

    const clickUrl = this.getClickUrl(adInfo.clickUrl, vrActType);
    AdAPI.navigateURL(clickUrl);

    console.log('[AdAction]openClickUrlOutside', clickUrl);
    return true;
  }

  public static openInAppJump(adInfo: AdInfo) {
    const inAppJumpLink = adInfo?.inAppJumpLink;
    if (!inAppJumpLink || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }

    AdAPI.openQQLive(inAppJumpLink);

    console.log('[AdAction]openInAppJump', inAppJumpLink);
    return true;
  }

  public static openQrcode(adInfo: AdInfo) {
    const qrCodeUrl = adInfo?.qrCodeUrl;
    if (!qrCodeUrl || !import.meta.env.EVO || !vnContext?.external) {
      return false;
    }

    const url = PROTOCOL + qrCodeUrl;
    AdAPI.openQQLive(url);

    console.log('[AdAction]openQrcode', url);
    return true;
  }

  // 保存小游戏打开记录
  private static saveMiniGame(adInfo: AdInfo, wxGameLink: string) {
    const { miniProgram } = adInfo;
    // 104 是小游戏
    if (adInfo.actionType !== 104 || !miniProgram) {
      return;
    }

    saveMiniGameInfo({
      appid: miniProgram.appid,
      icon_url: adInfo.avatarUrl || adInfo.coverPic,
      title: adInfo.advertiserName || adInfo.title,
      sub_title: adInfo.subTitle || adInfo.title,
      open_url: wxGameLink,
      path: miniProgram.path,
      btnText: adInfo.actionBtnText,
      qr_code_url: adInfo.qrCodeUrl,
    });
  }

  // 点击上报链接
  private static getClickUrl = (
    clickUrl?: string,
    actType?: AdVRActionType,
  ): string => {
    // 判断是否有落地页地址
    if (!clickUrl) {
      return '';
    }
    clickUrl = clickUrl.replace('__RETURN_TYPE__', '2');
    if (actType) {
      clickUrl = clickUrl.replace(/\bacttype=([^&]+)\b/, `acttype=${actType}`);
    }
    return clickUrl;
  };

  private static getHelperAmsLinkParams = (adInfo: AdInfo): TencentMiniProgramHelper.IAmsLinkParams => {
    const amsLinkParams: TencentMiniProgramHelper.IAmsLinkParams = {
      aid: adInfo.amsLinkParams?.aid || '',
      traceid: adInfo.amsLinkParams?.traceid || '',
      pid: adInfo.amsLinkParams?.pid || '',
      wx_type: Number(adInfo.amsLinkParams?.wx_type || ''),
      net_log_id: adInfo.amsLinkParams?.net_log_id || '',
    };
    return amsLinkParams;
  };
}
